<template>
    <div class="qty-stepper" :class="size" :title="editing ? null : $t('devis.v3.qty_edit')">
        <!--
            Le « − » n'existe qu'au-dessus de 1 : à 1, la place est prise par la corbeille.
            Retirer la ligne est donc le prolongement naturel du stepper, pas un second geste.
        -->
        <button
            v-if="isAboveOne"
            type="button"
            class="qty-btn"
            :disabled="disabled"
            :title="$t('devis.v3.qty_decrease')"
            @mousedown="guardFocus"
            @click="$emit('step', -1)"
        >
            <Minus :size="markSize" :stroke-width="2.6" />
        </button>
        <button
            v-else
            type="button"
            class="qty-btn qty-remove"
            :disabled="disabled"
            :title="removeTitle || $t('devis.v3.remove_line')"
            @mousedown="guardFocus"
            @click="$emit('remove')"
        >
            <Trash2 :size="trashSize" :stroke-width="1.9" />
        </button>

        <!--
            Deux états, un seul emplacement : au repos le nombre est un BOUTON (jamais un texte
            mort), au clic il devient un input sans les flèches natives — les seuls réglages de la
            quantité restent − et +, la saisie directe est un raccourci, pas le chemin principal.
        -->
        <input
            v-if="editing"
            ref="input"
            class="qty-input"
            :value="draft"
            type="number"
            min="1"
            step="1"
            inputmode="numeric"
            @input="$emit('input', $event.target.value)"
            @keyup.enter="$emit('commit')"
            @keyup.esc="$emit('cancel')"
            @blur="$emit('commit')"
        />
        <button v-else type="button" class="qty-value" :disabled="disabled" @click="$emit('edit-start')">
            {{ quantity }}
        </button>

        <button
            type="button"
            class="qty-btn qty-inc"
            :disabled="disabled"
            :title="$t('devis.v3.qty_increase')"
            @mousedown="guardFocus"
            @click="$emit('step', 1)"
        >
            <Plus :size="markSize" :stroke-width="2.8" />
        </button>
    </div>
</template>

<script>
import { Minus, Plus, Trash2 } from 'lucide-vue'

/**
 * Contrôle de quantité d'une ligne de devis : − / quantité / +.
 *
 * Reprend la maquette « Devis - Lignes produits » : un GROUPE unique et bordered (les trois
 * éléments sont jointifs, sans espace entre eux), dont le nombre est la pièce centrale.
 *
 * Deux états, pilotés par le parent :
 *   - `editing` false → le nombre est un bouton. Un clic émet `edit-start` ; le parent ouvre alors
 *     l'édition de la case (son `cellEdit` reste la source de vérité, comme les autres cases).
 *   - `editing` true  → le nombre devient un input SANS les flèches natives du navigateur : les
 *     seuls réglages restent les boutons, la saisie directe n'est qu'un raccourci.
 *
 * `@see Create.vue` — `hasQtyStepper` décide qui reçoit ce contrôle, `stepQuantity` écrit.
 */
export default {
    name: 'QtyStepper',
    components: { Minus, Plus, Trash2 },
    props: {
        /** Quantité courante. Un `display_ordre` de 0 n'a rien à voir là-dedans. */
        quantity: {
            type: Number,
            required: true,
        },
        /** `lg` sur une ligne principale, `sm` sur une option / un supplément. */
        size: {
            type: String,
            default: 'lg',
        },
        /** La case est-elle en cours d'édition ? */
        editing: {
            type: Boolean,
            default: false,
        },
        /** Valeur affichée dans l'input d'édition — alimentée par le `cellEdit` du parent. */
        draft: {
            type: [String, Number],
            default: 1,
        },
        /** Verrouillé pendant une écriture ou une édition de ligne. */
        disabled: {
            type: Boolean,
            default: false,
        },
        /** Infobulle de la corbeille — « Retirer du devis » vs « Retirer l'option ». */
        removeTitle: {
            type: String,
            default: '',
        },
        /**
         * Empêche les boutons de voler le focus au clic.
         *
         * Indispensable sur une ligne en CAPTURE de désignation : l'input du nom porte un
         * `blur` qui valide la capture, donc un simple clic sur « + » la terminerait — et, nom
         * encore vide, SUPPRIMERait le brouillon. Bloquer le `mousedown` empêche le changement de
         * focus, donc le blur, donc la sortie de capture : « + » ajuste alors la quantité de la
         * ligne qu'on est encore en train de nommer, comme dans la maquette.
         */
        holdFocus: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        // À 1 (ou moins, donnée héritée), le « − » laisse la place à la corbeille.
        isAboveOne() {
            return Number(this.quantity) > 1
        },
        // Les icônes suivent la boîte du bouton : 11px en lg, 10px en sm.
        markSize() {
            return this.size === 'sm' ? 10 : 11
        },
        // La corbeille est UN CRAN plus grande que le − / le + dans la maquette (12px en lg, 11px
        // en sm) : un pouce plus lisible sur une action destructive.
        trashSize() {
            return this.markSize + 1
        },
    },
    watch: {
        editing(on) {
            if (!on) return
            // Le parent émet `edit-start` puis bascule `editing` : on attend le rendu de l'input
            // avant de prendre le focus, sinon on viserait un nœud qui n'existe pas encore.
            this.$nextTick(() => {
                const el = this.$refs.input
                if (!el) return

                el.focus()
                // Curseur en FIN de saisie (et non `select()`) : passer de 1 à 100 ne demande pas
                // d'effacer le 1, on complète derrière.
                const at = String(el.value ?? '').length
                if (typeof el.setSelectionRange === 'function') {
                    el.setSelectionRange(at, at)
                }
            })
        },
    },
    methods: {
        /**
         * Neutralise le `mousedown` quand `holdFocus` est actif. Empêcher le défaut sur
         * `mousedown` empêche le navigateur de déplacer le focus — donc empêche le `blur` de
         * l'input voisin, et ce qu'il déclenche (cf. prop `holdFocus`).
         */
        guardFocus(e) {
            if (!this.holdFocus) return
            e.preventDefault()
        },
    },
}
</script>

<style scoped lang="scss">
/*
    Palette = jetons de la maquette (documentationAi/figma) :
    grey-200 #e2e6ec (bordure), grey-100 #eef1f5, grey-50 #f8f9fb, grey-500 #6b7480,
    grey-400 #9aa4b2, primary-100 #f3eafa, primary-500 #7d2dbd, primary-600 #6a26a0,
    primary-700 #571f84, secondary-500 #021832.
*/
.qty-stepper {
    display: inline-flex;
    align-items: center;
    height: 26px;
    background: #fff;
    border: 1px solid #e2e6ec;
    border-radius: 8px;
    overflow: hidden;
    vertical-align: middle;

    &.sm {
        height: 24px;
    }
}

.qty-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    padding: 0;
    color: #6b7480;
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition:
        background 0.12s ease,
        color 0.12s ease;

    &:hover:not(:disabled) {
        color: #021832;
        background: #eef1f5;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.qty-remove {
    color: #9aa4b2;

    &:hover:not(:disabled) {
        color: #e11d48;
        background: #fee9ee;
    }
}

.qty-inc {
    color: #7d2dbd;

    &:hover:not(:disabled) {
        color: #571f84;
        background: #f3eafa;
    }
}

/* Le nombre : pièce centrale du groupe, encadrée par ses deux voisins. */
.qty-value,
.qty-input {
    flex: 0 0 auto;
    width: 34px;
    height: 24px;
    padding: 0;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    text-align: center;
    border: none;
    border-right: 1px solid #e2e6ec;
    border-left: 1px solid #e2e6ec;
    outline: none;
}

.qty-value {
    color: #021832;
    background: transparent;
    cursor: pointer;
    transition: background 0.12s ease;

    &:hover:not(:disabled) {
        background: #f8f9fb;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

/* En édition : fond teinté pour lire d'un coup d'œil que la case est ouverte. */
.qty-input {
    color: #6a26a0;
    background: #f3eafa;
    appearance: textfield;
    -moz-appearance: textfield;

    /* Les flèches natives du navigateur sont SUPPRIMÉES : les seuls réglages sont ±. */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        margin: 0;
        -webkit-appearance: none;
    }
}

.sm {
    .qty-btn {
        width: 22px;
        height: 22px;
    }

    .qty-value,
    .qty-input {
        width: 32px;
        height: 22px;
        font-size: 12.5px;
    }
}
</style>
